import InteractivePrototype from './interactivePrototype.ts';
import * as Notation from '../notation.ts';
import utils from '../utils.ts';
import CountdownTimer from '../uTheory.Checkpoints/countdownTimer.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import * as Skills from '../uTheory.Skills/skills.ts';
import { toHTML } from '../uTheory.Font/font.ts';

class OrderOfSharpsOrFlats extends InteractivePrototype {
  constructor(displayDiv, options, userData) {
    super();
    this.timeAccomodation = userData?.accommodation?.time || 1;
    this.targetSpeedFromSkills = OrderOfSharpsOrFlats.registry.skills;
    this.answer = [];
    this.settings = {
      instructions: {
        ariaLive: 'alert',
      },
      editor: false,
      notation: false,
      piano: false,
      letters: {
        letterButtons: true,
        checkButton: false,
        trashButton: true,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      oneTryOnly: false,
    };

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    if (options.staff === true) {
      this.settings.editor = {
        fixedWidth: 150,
      };
      this.settings.letters = {
        letterButtons: false,
        customButtons: false,
        trashButton: true,
        checkButton: true,
        hotKeys: true,
      };
      this.settings.oneTryOnly = false;
    }
    // now call the prototype's init function:
    super.init(displayDiv, options).then(() => {
      // and add hotkeys for key sig if needed...
      if (this.optionsTrue('staff')) {
        this.letterPanel.createKeySigHotKeys();
      }
    });
  }

  initializeMusic() {
    if (this.options.staff === true) {
      const keySig = new Notation.KeySignature('C', 'Major');
      const clef = new Notation.Clef();
      const staff = new Notation.Staff(clef);
      const measure = new Notation.Measure();
      const voice = new Notation.Voice(measure);

      voice.setStaff(staff);

      this.keySig = keySig;
      this.clef = clef;
      this.staff = staff;
      this.measure = measure;
      this.voice = voice;
      this.music = new Notation.Music(staff, voice, keySig);
    }
  }

  initializeEditor() {
    super.initializeEditor();
    const customKeySig = new Notation.CustomKeySignature({ editor: this.editor, accidental: 'b' });
    this.customKeySig = customKeySig;
    this.music.staves[0].setCustomKeySignature(customKeySig);
  }

  generateQuestions() {
    const processQuestion = (questionString, index) => {
      /* jshint validthis:true */
      const questionParts = questionString.split('/');
      const answer = questionParts[0] === 'sharps' ? 'FCGDAEB' : 'BEADGCF';
      const clef = questionParts[1] || utils.shuffle(['treble', 'bass'])[0];
      const skills = this.calculateSkill(questionParts[0], clef);

      return {
        question: questionParts[0],
        clef,
        answer,
        text: index + 1,
        skills,
      };
    };

    const questionsFromSkills = () => {
      const skills = this.options.skills.map(Skills.makeString);
      const questions = [];
      if (skills.find((s) => s.includes('sharp'))) {
        questions.push('sharps/treble');
        questions.push('sharps/bass');
      }
      if (skills.find((s) => s.includes('flat'))) {
        questions.push('flats/treble');
        questions.push('flats/bass');
      }
      return questions;
    };

    const qg = new QuestionGenerator();

    this.questions =
      this.options.questions !== undefined
        ? this.options.questions.map(processQuestion)
        : qg
            .rejectDuplicates(questionsFromSkills(), this.options.maxQuestions || 4)
            .map(processQuestion);
    return this.questions;
  }

  createCustomDivs() {
    if (this.options.staff === true) {
      return;
    }

    this.createCustomDiv('div', 'timerDiv', 1);
    this.createCustomDiv('progress', 'progressDiv', 2);
    this.createCustomDiv('div', 'answerDiv', 3);

    this.testTimer = new CountdownTimer(this.timerDiv);
    this.progressDiv.max = 100;
  }

  createCustomDiv(element, elementId, index) {
    this[elementId] = document.createElement(element);

    const div = this[elementId];
    const styleOptions = {
      fontSize: '4vw',
      fontWeight: 'bold',
      textAlign: 'center',
      padding: '10px',
      margin: '0 auto',
      width: '100%',
    };

    div.id = elementId;
    div.innerHTML = ' ';

    utils.merge(div.style, styleOptions);

    this.displayDiv.insertBefore(div, this.displayDiv.children[index]);
  }

  newQuestion() {
    this.progressTracker.start(this.targetSpeed);

    const question = this.questions[this.questionNumber].question;

    this.question = question;
    const accidental = question === 'sharps' ? '#' : 'b';
    this.letterPanel.setLettersAccidental(accidental);

    if (this.optionsTrue('staff')) {
      const clef = this.questions[this.questionNumber].clef;
      this.clef.setName(clef);
      this.customKeySig.clear();
      this.customKeySig.unlockSignature();
      this.customKeySig.setAccidentalType(accidental);
      this.editor.renderer.render();
    } else {
      this.clearAnswer();
      this.startTimer();
    }
    this.questionPrompt = this.getQuestionPrompt();

    this.wrongAnswers = 0;
    super.newQuestion();
  }

  startTimer() {
    let count = 0;
    const tries = ['0:45', '0:30', '0:22', '0:15', '0:07.5'];
    const timerData = tries[this.questionNumber] || '0:07.5';
    const timeIntervalPerPercent =
      (parseFloat(timerData.split(':')[1]) / 100) * 1000 * this.timeAccomodation;
    this.progressDiv.value = 100;
    const progressing = () => {
      count++;
      this.progressDiv.value = 100 - count;
    };

    this.testTimer.set(timerData, this.timeAccomodation);
    this.testTimer.start();
    this.progressBar = window.setInterval(progressing, timeIntervalPerPercent);
    this.secondsAllowed = this.testTimer.durationMS / 1000;

    this.testTimer.onTimeout(() => {
      this.testTimer.stop();
      window.clearInterval(this.progressBar);
      this.checkAnswer(true);
      this.clearAnswer();
    });
  }

  getQuestionPrompt() {
    let questionPrompt;
    const question = this.questions[this.questionNumber].question;
    const staff = this.options.staff;
    const startPrompt = staff ? 'Draw' : 'Write';
    const testMode = this.optionsTrue('testMode');

    questionPrompt = `${startPrompt} order of <strong>${question}</strong>`;
    if (!staff) questionPrompt += ` in ${this.secondsAllowed} seconds.`;
    else questionPrompt += '.';

    if (!testMode) {
      questionPrompt += `<br /><small>Question #{question} of ${this.questions.length}</small>`;
    }

    return questionPrompt;
  }

  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      const answer =
        questionString === 'sharps' ? 'FCGDAEB' : questionString === 'flats' ? 'BEADGCF' : '';
      const clef = utils.shuffle(['treble', 'bass'])[0];
      const skills = this.calculateSkill(questionString, clef);
      const questions = [];

      questions.push({
        question: questionString,
        clef,
        answer,
        text: 1,
        skills,
      });

      this.questions = questions;
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  calculateSkill(question, clef) {
    if (this.options.staff === true) {
      return question === 'sharps'
        ? [`${clef}_order_sharps`, 'order_sharps']
        : question === 'flats'
        ? [`${clef}_order_flats`, 'order_flats']
        : '';
    }
    return question === 'sharps' ? 'order_sharps' : question === 'flats' ? 'order_flats' : '';
  }

  addListeners() {
    this.letterPanel.addLetterCallback(this.addLetterToAnswer, this);
    this.letterPanel.addCustomButtonCallback(this.keySigCallback, this);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);

    if (this.optionsTrue('staff')) {
      this.letterPanel.addKeySigCallback(this.keySigCallback, this);
    }
  }

  keySigCallback(value) {
    if (!this.optionsTrue('staff') && value === 'trash') {
      this.deleteLetter();
      return;
    }

    const listeners = {
      backspace: () => {
        this.editor.delete();
      },
      trash: () => {
        this.editor.delete();
      },
      left: () => {
        this.customKeySig.selectLeft();
        this.editor.redraw();
      },
      right: () => {
        this.customKeySig.selectRight();
        this.editor.redraw();
      },
      up: () => {
        this.customKeySig.moveUp();
        this.editor.redraw();
      },
      down: () => {
        this.customKeySig.moveDown();
        this.editor.redraw();
      },
    };

    if (listeners.hasOwnProperty(value)) {
      listeners[value]();
    }
  }

  addLetterToAnswer(letter) {
    this.answer.push(letter);
    this.displayAnswer();
    if (this.answer.length === 7) {
      this.checkAnswer();
    }
  }

  deleteLetter() {
    if (this.answer.length > 0) {
      this.answer.pop();
      this.displayAnswer();
    }
  }

  displayAnswer() {
    const answer = this.answer;

    const joinChar =
      this.question === 'sharps' ? toHTML('accidental', '#') : toHTML('accidental', 'b');
    if (answer.length === 0) {
      this.answerDiv.removeAttribute('aria-live');
    } else {
      this.answerDiv.setAttribute('aria-live', 'polite');
    }
    this.answerDiv.innerHTML = (answer.length === 0 ? '_' : answer.join(`${joinChar} `)) + joinChar;
  }

  checkAnswer(outOfTime = false) {
    if (this.options.staff) {
      this.checkStaff(outOfTime);
    } else {
      this.checkLettersOnly(outOfTime);
    }
  }

  checkLettersOnly(outOfTime = false) {
    const testMode = this.optionsTrue('testMode');
    const rightAnswer = this.questions[this.questionNumber].answer;
    const userAnswer = this.answer.join('');
    const correct = rightAnswer === userAnswer;

    if (testMode === true) {
      this.checkpointAnswer();
      this.clearAnswer();
      return;
    }

    if (correct) {
      this.testTimer.stop();
      window.clearInterval(this.progressBar);
      this.getAnswerAndData(true);
      this.rightAnswer(1000);
    } else {
      this.testTimer.stop(); // restarts timer...
      window.clearInterval(this.progressBar);
      this.startTimer();
      this.getAnswerAndData(false);
      this.clearAnswer();
      this.wrongAnswer(
        undefined,
        outOfTime ? { alternateAriaText: "Time's up. Start over!" } : undefined
      );
    }
  }

  checkStaff() {
    const keyCheck = this.customKeySig.checkKey(this.clef);
    const validKey = keyCheck instanceof Notation.KeySignature;
    const keyNotes = this.customKeySig.getKeyNotes();
    const userAnswer = [];
    keyNotes.forEach((note) => {
      userAnswer.push(note.letter);
    });
    const rightAnswer = this.questions[this.questionNumber].answer;
    const correct = userAnswer.join('') === rightAnswer && validKey;

    if (correct) {
      this.getAnswerAndData(true);
      this.rightAnswer(1000);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  wrongAnswer(hintText, alternateAriaText) {
    if (this.wrongAnswers > 0 && this.optionsTrue('staff')) {
      const key =
        this.currentQuestion.question === 'sharps'
          ? new Notation.KeySignature('C#')
          : new Notation.KeySignature('Cb');
      const clef = this.currentQuestion.clef;
      hintText = this.customKeySig.hint(key, clef);
      this.editor.renderer.render();
    }
    this.wrongAnswers++;
    super.wrongAnswer(hintText, alternateAriaText);
    //      this.newQuestion();
  }

  clearAnswer() {
    this.answer = [];
    this.answerDiv.innerHTML = '?';
  }

  playAnswer() {}

  getAnswerAndData(correct) {
    const correctAnswer = this.questions[this.questionNumber].answer;
    const answerAndData = {
      question: this.questions[this.questionNumber].question,
      questionType: 'OrderOfSharpsOrFlats',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: this.answer.join(''),
      studentAnswerSVG: this.options.staff === true ? this.editor.exportSVG() : null,
      correctAnswerText: correctAnswer,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.questions[this.questionNumber].skills,
    };

    return answerAndData;
  }

  exit() {
    if (this.options.staff !== true) {
      this.testTimer.stop();
    }
    super.exit();
  }

  gameOver() {
    if (this.options.staff === true) {
      this.customKeySig.lockSignature();
      this.editor.redraw();
    } else if (this.options.staff !== true) {
      this.displayDiv.removeChild(this.answerDiv);
      this.displayDiv.removeChild(this.timerDiv);
      this.displayDiv.removeChild(this.progressDiv);
      this.testTimer.stop();
    }

    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);

    super.gameOver();
  }
}

OrderOfSharpsOrFlats.registry = {
  description: 'Draw the order of sharps/flats',
  options: {
    maxQuestions: 6,
    staff: true,
  },
  skills: [
    'order_sharps',
    'treble_order_sharps',
    'bass_order_sharps',
    'order_flats',
    'treble_order_flats',
    'bass_order_flats',
  ],
};

export default OrderOfSharpsOrFlats;
